import React from 'react';

import { useQuery } from "react-query";

// see : https://tanstack.com/query/latest/docs/react/guides/paginated-queries
// see:  https://tanstack.com/query/v3/docs/react/guides/paginated-queries

import { fetchArticlesGestionAdministration } from 'utils/queries';

import theme from 'theme';

import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EuroIcon from '@mui/icons-material/Euro';
import PersonIcon from '@mui/icons-material/Person';


import TriStateFilter from 'components/Shared/TriStateFilter';
import ArticleWebLink from 'components/Shared/ArticleWebLink';
import GeswebLink from 'components/Shared/GeswebLink'

import MultipleItemsBadgeAndPopover from 'components/Shared/MultipleItemsBadgeAndPopover';
import ArticleARisquePicto from 'components/Shared/ArticleARisquePicto';


const formatPrix = (prix) => {
    if (prix === null || prix === undefined) return null;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(prix);
};


const ArticleRow = ({ article }) => {

    const [open, setOpen] = React.useState(false);

    const mails = [
        { label: 'Chef produit', value: article.mailChefProduit },
        { label: 'Assistant 1', value: article.mailAssistantChefProduit },
        { label: 'Assistant 2', value: article.mailAssistantChefProduit2 },
    ].filter(m => m.value);

    const prixAchat = formatPrix(article.prixAchat);
    const prixRevient = formatPrix(article.prixRevient);
    const aFournisseur = article.codeFournisseur || article.nomFournisseur;
    const aPrix = prixAchat || prixRevient;
    const aContacts = mails.length > 0;

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ width: '140px' }}>
                    <GeswebLink cle={article.reference} structure='article' mode='button' label={article.reference} environment={null} />
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{article.designation}</TableCell>
                <TableCell>{article.marquePropre ? 'oui' : ''}</TableCell>
                <TableCell>{article.publieWeb ? (<ArticleWebLink reference={article.reference} />) : ''}</TableCell>
                <TableCell>{article.perissable ? 'oui' : ''}</TableCell>
                <TableCell>{article.retourExclu ? 'oui' : ''}</TableCell>
                <TableCell>{article.conditionSAV ? `${article.fournisseur.code} - ${article.fournisseur.nom}` : ''}</TableCell>
                <TableCell>{article.gererEnStock ? 'oui' : ''}</TableCell>
                <TableCell>{article.codeGestion ? 'oui' : ''}</TableCell>
                <TableCell>{article.aRisque ? <ArticleARisquePicto /> : ''}</TableCell>
                <TableCell>{article.composants.length > 0 ? (<MultipleItemsBadgeAndPopover badge={false} label="oui" title={`Article du kit ${article.reference} - ${article.designation}`} items={article.composants} display={a => `${a.reference} - ${a.designation} (X${a.quantite})`} />) : ''}</TableCell>
                <TableCell>{article.compose.length > 0 ? (<MultipleItemsBadgeAndPopover badge={false} label="oui" title="Fait parti des kits" items={article.compose} display={a => `${a.reference} - ${a.designation} (X${a.quantite})`} />) : ''}</TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '10px' }}>

                            {aFournisseur && (
                                <Box sx={{ gridColumn: 'span 4', padding: 1, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
                                    <Typography variant="subtitle1" sx={{ color: theme.palette.primary.light, fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <StorefrontIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        Fournisseur
                                    </Typography>
                                    <Typography variant="body2">
                                        {article.codeFournisseur && <><strong>{article.codeFournisseur}</strong>{article.nomFournisseur && ' - '}</>}
                                        {article.nomFournisseur}
                                    </Typography>
                                </Box>
                            )}

                            {aPrix && (
                                <Box sx={{ gridColumn: 'span 4', padding: 1, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
                                    <Typography variant="subtitle1" sx={{ color: theme.palette.primary.light, fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <EuroIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        Prix
                                    </Typography>
                                    {prixAchat && (
                                        <Typography variant="body2"><strong>Achat :</strong> {prixAchat}</Typography>
                                    )}
                                    {prixRevient && (
                                        <Typography variant="body2"><strong>Revient :</strong> {prixRevient}</Typography>
                                    )}
                                </Box>
                            )}

                            {aContacts && (
                                <Box sx={{ gridColumn: 'span 4', padding: 1, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
                                    <Typography variant="subtitle1" sx={{ color: theme.palette.primary.light, fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        Contacts produit
                                    </Typography>
                                    {mails.map(m => (
                                        <Typography key={m.label} variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>{m.label} :</strong>{' '}
                                            <Link href={`mailto:${m.value}`} underline="hover">{m.value}</Link>
                                        </Typography>
                                    ))}
                                </Box>
                            )}

                            {!aFournisseur && !aPrix && !aContacts && (
                                <Box sx={{ gridColumn: 'span 12', padding: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                    Aucune information complémentaire disponible.
                                </Box>
                            )}

                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};


const AdministrationArticlesGestion = () => {

    const [page, setPage] = React.useState(0);
    const [nbParPage, setNbParPage] = React.useState(10);

    const [filtreReference, setFiltreReference] = React.useState('');

    const [filtrePerissable, setFiltrePerissable] = React.useState(null);
    const [filtrePublieWeb, setFiltrePublieWeb] = React.useState(null);
    const [filtreMarquePropre, setFiltreMarquePropre] = React.useState(null); 
    const [filtreRetourExclu, setFiltreRetourExclu] = React.useState(null); 
    const [filtreConditionSAV, setFiltreConditionSAV] = React.useState(null); 
    const [filtreGererEnStock, setFiltreGererEnStock] = React.useState(null); 
    const [filtreCompose, setFiltreCompose] = React.useState(null);
    const [filtreComposant, setFiltreComposant] = React.useState(null);
    const [filtreCodeGestion, setFiltreCodeGestion] = React.useState(null);
    const [filtreARisque, setFiltreARisque] = React.useState(null);


    const { isLoading, isError, error, data } =
        useQuery({
            queryKey: [
                'articlesGestion',
                page,
                nbParPage,
                filtreReference,
                filtrePerissable,
                filtrePublieWeb,
                filtreMarquePropre,
                filtreRetourExclu,
                filtreConditionSAV,
                filtreGererEnStock,
                filtreCompose,
                filtreComposant,
                filtreCodeGestion,
                filtreARisque
            ],
            queryFn: () => fetchArticlesGestionAdministration({
                page,
                nbParPage,
                filtreReference,
                filtrePerissable,
                filtrePublieWeb,
                filtreMarquePropre,
                filtreRetourExclu,
                filtreConditionSAV,
                filtreGererEnStock,
                filtreCompose,
                filtreComposant,
                filtreCodeGestion,
                filtreARisque
            }),
            keepPreviousData: true,
        })


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setNbParPage(+event.target.value);
        setPage(0);
    };

    return (
        <>
            <Typography gutterBottom variant="h5">
                Visualisation des articles
            </Typography>


            {isLoading ? (
                <div>Chargement...</div>
            ) : isError ? (
                <div>Erreur: {error.message}</div>
            ) : (
                <span></span>
            )}

            <Paper>

                {data ? (
                    <TableContainer sx={{ maxHeight: '100%' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '140px' }}>Référence</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey' }}>Désignation</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Marque propre</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Publié WEB</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Périssable</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Retour exclu</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Condition SAV</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Gerer en stock</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Code gestion</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>À risque</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Composé</TableCell>
                                    <TableCell sx={{ backgroundColor: 'lightslategrey', width: '100px' }}>Composant</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ width: '140px' }}>
                                        <TextField
                                            value={filtreReference}
                                            onChange={e => { setFiltreReference(e.target.value) }}
                                        >
                                        </TextField>
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreMarquePropre}
                                            onChange={newValue => { setFiltreMarquePropre(newValue) }}
                                        />
                                        </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtrePublieWeb}
                                            onChange={newValue => { setFiltrePublieWeb(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtrePerissable}
                                            onChange={newValue => { setFiltrePerissable(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreRetourExclu}
                                            onChange={newValue => { setFiltreRetourExclu(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreConditionSAV}
                                            onChange={newValue => { setFiltreConditionSAV(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreGererEnStock}
                                            onChange={newValue => { setFiltreGererEnStock(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreCodeGestion}
                                            onChange={newValue => { setFiltreCodeGestion(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreARisque}
                                            onChange={newValue => { setFiltreARisque(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreCompose}
                                            onChange={newValue => { setFiltreCompose(newValue) }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TriStateFilter
                                            value={filtreComposant}
                                            onChange={newValue => { setFiltreComposant(newValue) }}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data.articles
                                    .map((article) => (
                                        <ArticleRow
                                            key={article.reference}
                                            article={article}
                                        />
                                    ))}
                            </TableBody>

                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={data.nbFiltres}
                            rowsPerPage={nbParPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                ) : <span>...</span>
                }

            </Paper>


        </>

        );

};





//AdministrationFournisseurs.propTypes = {
//};

export default AdministrationArticlesGestion;